// Module Transition Map for AcquireOS MVP
// Defines entry conditions, outputs, and triggers for each module

export const MODULES = {
  entry: {
    name: "Deal Entry",
    entryCondition: "User logged in",
    output: "User selects upload path or browses live deals",
    trigger: "User chooses manual upload or selects a live deal",
    requiredFields: []
  },
  1: {
    name: "Deal Lead Generation",
    entryCondition: "User logged in and navigates to deal generation",
    output: "Selected deal data (Price, Location, Units, NOI, Initial AI score)",
    trigger: "User selects a deal from available options",
    requiredFields: []
  },
  2: {
    name: "Market Intelligence",
    entryCondition: "Always accessible from header",
    output: "Market data, state scores, cap rate trends, market pulse",
    trigger: "User opens Market Intelligence panel from header",
    requiredFields: [],
    headerPinned: true
  },
  3: {
    name: "Deal Intake & Document Upload",
    entryCondition: "Deal selected or manual upload path chosen",
    output: "Cash flow display, income vs expenses, scenario toggles",
    trigger: "User reviews financial modeling view",
    requiredFields: ["rawDealData", "extractedFinancials"]
  },
  4: {
    name: "Underwriting Engine",
    entryCondition: "Financial modeling view available from Module 3",
    output: "Calculated underwriting metrics (NOI, Cap Rate, DSCR, IRR)",
    trigger: "Underwriting calculations completed",
    requiredFields: ["rawDealData", "extractedFinancials", "financialModeling"]
  },
  5: {
    name: "Deal Score & Verdict",
    entryCondition: "Underwriting metrics available from Module 4",
    output: "Numerical score, verdict, strengths & risks, offer price",
    trigger: "Deal scoring algorithm completed",
    requiredFields: ["rawDealData", "extractedFinancials", "financialModeling", "underwritingMetrics"]
  },
  6: {
    name: "Lender Match Engine",
    entryCondition: "Deal score and verdict generated in Module 5",
    output: "Ranked lenders with loan terms and match scores",
    trigger: "Lender matching algorithm completed",
    requiredFields: ["rawDealData", "extractedFinancials", "underwritingMetrics", "scoreAndOffer"]
  },
  7: {
    name: "Deal Syndication Engine",
    entryCondition: "Lender selected in Module 6",
    output: "Co-investor equity allocations, capital stack, funding progress",
    trigger: "Syndication structure finalized",
    requiredFields: ["rawDealData", "lenderMatches"]
  },
  8: {
    name: "Loan Package Generator",
    entryCondition: "Syndication reviewed in Module 7",
    output: "Professional loan package document",
    trigger: "Document generation completed",
    requiredFields: ["rawDealData", "extractedFinancials", "underwritingMetrics", "scoreAndOffer", "lenderMatches"]
  },
  9: {
    name: "Outreach & Communication",
    entryCondition: "Loan package generated in Module 8",
    output: "Email scripts, call scripts, SMS templates",
    trigger: "Communication templates generated",
    requiredFields: ["rawDealData", "extractedFinancials", "scoreAndOffer", "lenderMatches", "loanPackage"]
  },
  10: {
    name: "Actions & Follow-up",
    entryCondition: "Communication templates ready in Module 9",
    output: "Task list with priorities and status tracking",
    trigger: "Action plan generated",
    requiredFields: ["rawDealData", "extractedFinancials", "scoreAndOffer", "lenderMatches"]
  },
  11: {
    name: "LOI Generator",
    entryCondition: "Action plan created in Module 10",
    output: "Letter of Intent document with offer terms",
    trigger: "LOI document generated",
    requiredFields: ["rawDealData", "extractedFinancials", "scoreAndOffer"]
  },
  12: {
    name: "PSA Assistant",
    entryCondition: "LOI generated in Module 11",
    output: "Purchase and Sale Agreement document",
    trigger: "PSA document generated",
    requiredFields: ["rawDealData", "extractedFinancials", "scoreAndOffer", "loiDocument"]
  },
  13: {
    name: "Escrow & Title Workflow",
    entryCondition: "PSA executed in Module 12",
    output: "Escrow file opened, title search initiated",
    trigger: "Escrow process started",
    requiredFields: ["rawDealData", "extractedFinancials", "psaDocument"]
  },
  14: {
    name: "Due Diligence & Title Clearance",
    entryCondition: "Escrow opened in Module 13",
    output: "Due diligence completed, title cleared",
    trigger: "All due diligence items resolved",
    requiredFields: ["rawDealData", "extractedFinancials", "escrowDetails"]
  },
  15: {
    name: "Financing, Closing & Recording",
    entryCondition: "Title cleared in Module 14",
    output: "Loan funded, closing completed, deed recorded",
    trigger: "Closing transaction completed",
    requiredFields: ["rawDealData", "extractedFinancials", "lenderMatches", "titleClearance"]
  },
  16: {
    name: "Asset Plan & Hold",
    entryCondition: "Property acquired in Module 15",
    output: "KPI dashboard for property management",
    trigger: "Asset management plan activated",
    requiredFields: ["rawDealData", "extractedFinancials", "closingDetails"]
  },
  17: {
    name: "Exit Strategy",
    entryCondition: "Asset management active in Module 16",
    output: "Exit strategy analysis comparing sale, refinance, hold, and 1031 exchange options",
    trigger: "Exit strategy analysis completed",
    requiredFields: ["rawDealData", "extractedFinancials", "assetManagement"]
  }
};

// Pipeline stages with their display names and order
// Note: Module 2 (Market Intelligence) is header-pinned, not a pipeline step
// Correct order: Intake → Underwriting (3) → Deal Score (4) → Lenders → ...
export const PIPELINE_STAGES = [
  { id: 'entry', name: "Deal Entry",                   shortName: "Entry" },
  { id: 1,       name: "Deal Lead Generation",          shortName: "Lead" },
  { id: 3,       name: "Deal Intake & Documents",       shortName: "Intake" },
  { id: 4,       name: "Underwriting Engine",           shortName: "UW" },     // Step 3
  { id: 5,       name: "Deal Score & Verdict",          shortName: "Score" },  // Step 4
  { id: 6,       name: "Lender Matching",               shortName: "Lend" },
  { id: 7,       name: "Deal Syndication",              shortName: "Synd" },
  { id: 8,       name: "Loan Package",                  shortName: "Loan" },
  { id: 9,       name: "Outreach",                      shortName: "Contact" },
  { id: 10,      name: "Actions & Follow-up",           shortName: "Act" },
  { id: 11,      name: "LOI Generation",                shortName: "LOI" },
  { id: 12,      name: "PSA Assistant",                 shortName: "PSA" },
  { id: 13,      name: "Escrow & Title",                shortName: "Esc" },
  { id: 14,      name: "Due Diligence",                 shortName: "DD" },
  { id: 15,      name: "Closing & Recording",           shortName: "Close" },
  { id: 16,      name: "Asset Plan & Hold",             shortName: "Asset" },
  { id: 17,      name: "Exit Strategy",                 shortName: "Exit" }
];
